import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound, LogOut, User } from 'lucide-react';
import { useSesion } from '../../sesion/ProveedorDeSesion';
import { Dialogo } from '../ui/dialogo';
import { FormularioDeClave } from '../FormularioDeClave';

/** Las iniciales del correo: `luis@donluis.test` → `LD`. */
function iniciales(correo: string): string {
  const [antes = '', dominio = ''] = correo.split('@');
  return `${antes.charAt(0)}${dominio.charAt(0)}`.toUpperCase();
}

/**
 * Quién está operando, y cómo se sale.
 *
 * ## Por qué «Salir» dejó de estar suelto
 *
 * Estaba a la vista todo el día al lado del correo, y es la única acción de la barra que **no se
 * puede deshacer**: en un mostrador con gente esperando, un botón así al alcance del codo se
 * aprieta solo. Adentro de un menú sigue estando a un toque y deja de estar al paso.
 */
export function MenuDeUsuario(): React.ReactElement {
  const { t } = useTranslation();
  const { perfil, salir } = useSesion();
  const [abierto, setAbierto] = useState(false);
  const [cambiandoClave, setCambiandoClave] = useState(false);
  const contenedor = useRef<HTMLDivElement>(null);

  // Se cierra al tocar afuera y con Escape. Sin las dos, un menú abierto tapa la pantalla hasta
  // que alguien adivina que hay que volver a tocar el mismo botón.
  useEffect(() => {
    if (!abierto) return undefined;
    const afuera = (evento: MouseEvent): void => {
      if (!contenedor.current?.contains(evento.target as Node)) setAbierto(false);
    };
    const conEscape = (evento: KeyboardEvent): void => {
      if (evento.key === 'Escape') setAbierto(false);
    };
    document.addEventListener('mousedown', afuera);
    document.addEventListener('keydown', conEscape);
    return () => {
      document.removeEventListener('mousedown', afuera);
      document.removeEventListener('keydown', conEscape);
    };
  }, [abierto]);

  const correo = perfil?.correo ?? '';

  return (
    <div className="relative" ref={contenedor}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={abierto}
        onClick={() => {
          setAbierto((previo) => !previo);
        }}
        className="flex items-center gap-2 rounded-marco px-1.5 py-1 text-cuerpo transition-colors hover:bg-acento-suave"
      >
        <span className="grid size-7 shrink-0 place-items-center rounded-full bg-acento text-apoyo font-semibold text-white">
          {correo === '' ? <User className="size-4" aria-hidden /> : iniciales(correo)}
        </span>
        <span className="hidden text-texto-tenue sm:inline">{correo}</span>
      </button>

      {abierto ? (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1 min-w-48 rounded-marco border border-borde bg-superficie p-1 shadow-lg"
        >
          <p className="px-2 py-1.5 text-apoyo text-texto-tenue sm:hidden">{correo}</p>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setAbierto(false);
              setCambiandoClave(true);
            }}
            className="flex w-full items-center gap-2 rounded-marco px-2 py-1.5 text-cuerpo transition-colors hover:bg-acento-suave"
          >
            <KeyRound className="size-4" aria-hidden />
            {t('clave.miClave')}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => void salir()}
            className="flex w-full items-center gap-2 rounded-marco px-2 py-1.5 text-cuerpo text-mal transition-colors hover:bg-mal-suave"
          >
            <LogOut className="size-4" aria-hidden />
            {t('acciones.salir')}
          </button>
        </div>
      ) : null}

      <Dialogo
        abierto={cambiandoClave}
        titulo={t('clave.titulo')}
        descripcion={t('clave.ayuda')}
        onCerrar={() => {
          setCambiandoClave(false);
        }}
      >
        <FormularioDeClave
          onListo={() => {
            setCambiandoClave(false);
          }}
        />
      </Dialogo>
    </div>
  );
}
